// src/frontend/src/components/Simulation/ScenarioSelector.tsx
import React, { useState, useEffect } from 'react';
import { SimulationScenario, ScenarioCategory, Difficulty, StakeholderType, LearningObjective } from '../../types/simulation';
import { scenarioService } from '../../api/simulationApi';

interface ScenarioSelectorProps {
  onSelectScenario: (scenario: SimulationScenario) => void;
}

const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({ onSelectScenario }) => {
  const [scenarios, setScenarios] = useState<SimulationScenario[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  
  // Filter states
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [stakeholderTypeFilter, setStakeholderTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    fetchScenarios();
  }, []);

  const fetchScenarios = async () => {
    try {
      setIsLoading(true);
      const response = await scenarioService.getScenarios();
      setScenarios(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch scenarios');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter scenarios based on selected filters and search query
  const filteredScenarios = scenarios.filter(scenario => {
    let matches = true;
    
    // Category filter
    if (categoryFilter && scenario.category !== categoryFilter) {
      matches = false;
    }
    
    // Difficulty filter
    if (difficultyFilter && scenario.difficulty !== difficultyFilter) {
      matches = false;
    }
    
    // Stakeholder type filter
    if (stakeholderTypeFilter && scenario.stakeholderType !== stakeholderTypeFilter) {
      matches = false;
    }
    
    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        scenario.title.toLowerCase().includes(query) ||
        scenario.description.toLowerCase().includes(query);
      
      if (!matchesSearch) {
        matches = false;
      }
    }
    
    return matches;
  });

  // Get difficulty badge color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case Difficulty.EASY:
        return 'bg-green-100 text-green-800';
      case Difficulty.MEDIUM:
        return 'bg-yellow-100 text-yellow-800';
      case Difficulty.HARD:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Format enum values for display
  const formatEnumValue = (value: string): string => {
    return value
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-6">Select a Scenario</h2>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Search and Filters */}
      <div className="mb-6">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search scenarios..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full p-2 border rounded-md"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="">All Categories</option>
              {Object.values(ScenarioCategory).map((category) => (
                <option key={category} value={category}>
                  {formatEnumValue(category)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Stakeholder Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stakeholder Type
            </label>
            <select
              value={stakeholderTypeFilter}
              onChange={(e) => setStakeholderTypeFilter(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="">All Stakeholder Types</option>
              {Object.values(StakeholderType).map((type) => (
                <option key={type} value={type}>
                  {formatEnumValue(type)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Difficulty Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Difficulty
            </label>
            <select
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
              className="w-full p-2 border rounded-md"
            >
              <option value="">All Difficulties</option>
              {Object.values(Difficulty).map((difficulty) => (
                <option key={difficulty} value={difficulty}>
                  {formatEnumValue(difficulty)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Scenarios List */}
      {isLoading ? (
        <div className="text-center py-8">
          <p>Loading scenarios...</p>
        </div>
      ) : filteredScenarios.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No scenarios match your filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredScenarios.map((scenario) => (
            <div 
              key={scenario._id} 
              className="border rounded-lg overflow-hidden hover:shadow-md cursor-pointer transition-shadow"
              onClick={() => onSelectScenario(scenario)}
            >
              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-medium">{scenario.title}</h3>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full ${getDifficultyColor(scenario.difficulty)}`}>
                    {formatEnumValue(scenario.difficulty)}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">{scenario.description}</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  <span className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                    {formatEnumValue(scenario.category)}
                  </span>
                  <span className="inline-block px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                    {formatEnumValue(scenario.stakeholderType)}
                  </span>
                </div>
                <div className="text-sm text-gray-500">
                  Learning objectives: {scenario.learningObjectives.slice(0, 2).map(obj => formatEnumValue(obj)).join(', ')}
                  {scenario.learningObjectives.length > 2 ? ` +${scenario.learningObjectives.length - 2} more` : ''}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScenarioSelector;
